/**
 * DonateDialog Component
 *
 * Integrated with Zeffy donation forms
 * Embeds Zeffy form directly in MUI dialog via iframe
 */

import React, { useEffect } from 'react'
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material'
import {
  Close as CloseIcon,
  VolunteerActivism as VolunteerActivismIcon,
} from '@mui/icons-material'
import { DonationContent } from '@/data/types'
import { trackEvent } from '@/utils/analytics'
import { getZeffyFormLink, isZeffyConfigured } from '@/data/zeffy-config'

export interface DonateDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean

  /**
   * Callback when the dialog should close
   */
  onClose: () => void

  /**
   * Donation content
   */
  content: DonationContent

  /**
   * Callback when user attempts to donate (kept for API compatibility)
   */
  onDonateClick?: (optionId: string, amount?: number) => void
}

const DonateDialog: React.FC<DonateDialogProps> = ({
  open,
  onClose,
  content,
}) => {
  const zeffyConfigured = isZeffyConfigured()
  const zeffyFormLink = getZeffyFormLink()

  // Remove ?modal=true from URL for iframe embed
  const zeffyIframeUrl = zeffyFormLink.replace('?modal=true', '')

  useEffect(() => {
    if (open && zeffyConfigured) {
      trackEvent('donation_popup_opened', {
        source: 'donate_dialog',
        zeffy_configured: true,
      })
    }
  }, [open, zeffyConfigured])

  // If Zeffy not configured, show fallback message
  if (!zeffyConfigured) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              textAlign: 'center',
              py: 3,
            }}
          >
            <VolunteerActivismIcon
              sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              {content.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Donation integration is being configured. Please check back soon!
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  // Show dialog with embedded Zeffy form iframe
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        Support Our Mission
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* Embed Zeffy form directly via iframe */}
        <iframe
          src={zeffyIframeUrl}
          title="Zeffy Donation Form"
          style={{
            width: '100%',
            height: '550px',
            border: 'none',
            minHeight: '500px',
          }}
          loading="lazy"
        />
      </DialogContent>
    </Dialog>
  )
}

export default DonateDialog
